import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useForm, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { FormInput } from "@/components/ui/form-input";
import { FormTextarea } from "@/components/ui/form-textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelect } from "@/components/ui/multi-select";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Supplier {
  id: string;
  name: string;
  email: string;
}

interface SupplierProfile {
  id?: string;
  supplier_id: string;
  business_name: string;
  logo_url: string;
  description: string;
  website: string;
  gst_number: string;
  pan_number: string;
  verification_status: "pending" | "verified" | "rejected";
  slug: string;
  address: string;
  establishment_year: number;
  legal_status: string;
  nature_of_business: string;
  annual_turnover: string;
  employee_count: string;
  import_export_code: string;
  gst_registration_date: string;
  certifications: any[];
  product_categories: string[];
  mission: string;
  vision: string;
  about: string;
  main_markets: string[];
  trust_seal_verified: boolean;
  machinesearch_certification: boolean;
}

interface SupplierDocument {
  id: string;
  document_type: string;
  document_url: string;
  verified: boolean;
}

interface SupplierCertification {
  id: string;
  name: string;
  issuer: string;
  issued_date: string;
  expiry_date: string;
  verification_status: string;
}

const defaultValues: SupplierProfile = {
  supplier_id: "",
  business_name: "",
  logo_url: "",
  description: "",
  website: "",
  gst_number: "",
  pan_number: "",
  verification_status: "pending",
  slug: "",
  address: "",
  establishment_year: new Date().getFullYear(),
  legal_status: "",
  nature_of_business: "",
  annual_turnover: "",
  employee_count: "",
  import_export_code: "",
  gst_registration_date: "",
  certifications: [],
  product_categories: [],
  mission: "",
  vision: "",
  about: "",
  main_markets: [],
  trust_seal_verified: false,
  machinesearch_certification: false,
};

interface SupplierProfileFormProps {
  supplierId?: string;
  onChange?: () => void;
}

export default function SupplierProfileForm({ supplierId, onChange }: SupplierProfileFormProps) {
  const { register, handleSubmit, reset, setValue, watch, control, formState: { errors, isSubmitting } } = useForm({ defaultValues });
  const [slugError, setSlugError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<SupplierDocument[]>([]);
  const [certifications, setCertifications] = useState<SupplierCertification[]>([]);
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  // Utility to slugify a string
  function slugify(str: string) {
    return str
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .replace(/--+/g, "-");
  }

  // Load suppliers list
  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        // Emergency bypass due to RLS infinite recursion
        console.log("Using emergency bypass for suppliers list");
        
        const emergencySuppliers = [
          {
            id: "93794bbb-34c2-48f4-a292-1f55434bcdba",
            name: "Admin User", 
            email: "admin@machinesearch.com"
          }
        ];
        
        setSuppliers(emergencySuppliers);
        
        /*
        // Original database query - disabled due to RLS issues
        const { data, error } = await supabase
          .from("accounts")
          .select("id, name, email")
          .eq("user_type", "both")
          .order("name");
        
        if (error) throw error;
        setSuppliers(data || []);
        */
      } catch (error) {
        console.error("Error loading suppliers:", error);
        // Don't show error toast for emergency bypass
        // showToast("Failed to load suppliers list", "error");
      }
    };

    loadSuppliers();
  }, []);

  // Load profile data if supplierId is provided
  useEffect(() => {
    if (supplierId) {
      setLoading(true);
      
      // Emergency bypass due to RLS infinite recursion
      if (supplierId === "93794bbb-34c2-48f4-a292-1f55434bcdba") {
        console.log("Using emergency bypass for supplier profile form");
        
        const emergencyData = {
          id: "93794bbb-34c2-48f4-a292-1f55434bcdba",
          supplier_id: "93794bbb-34c2-48f4-a292-1f55434bcdba",
          business_name: "Supplier Company Pvt Ltd",
          logo_url: "https://dummyimage.com/100x100/000/fff.png&text=Logo",
          description: "Leading supplier of industrial machinery.",
          website: "https://supplier-company.com",
          gst_number: "",
          pan_number: "",
          verification_status: "verified" as const,
          slug: "supplier-company-pvt-ltd",
          address: "",
          establishment_year: 2020,
          legal_status: "Private Limited Company",
          nature_of_business: "Manufacturing",
          annual_turnover: "1-5 Crores",
          employee_count: "10-50",
          import_export_code: "",
          gst_registration_date: "",
          certifications: [],
          product_categories: [],
          mission: "",
          vision: "",
          about: "",
          main_markets: [],
          trust_seal_verified: false,
          machinesearch_certification: false,
        };
        
        reset(emergencyData);
        setDocuments([]);
        setCertifications([]);
        setLoading(false);
      } else {
        // Original database queries - commented out due to RLS issues
        /*
        Promise.all([
          // Load profile
          supabase
            .from("supplier_profiles")
            .select("*")
            .eq("id", supplierId)
            .single(),
          // Load documents
          supabase
            .from("supplier_documents")
            .select("*")
            .eq("supplier_id", supplierId),
          // Load certifications
          supabase
            .from("supplier_certifications")
            .select("*")
            .eq("supplier_id", supplierId)
        ]).then(([profile, docs, certs]) => {
          if (profile.data) reset(profile.data);
          if (docs.data) setDocuments(docs.data);
          if (certs.data) setCertifications(certs.data);
          setLoading(false);
        });
        */
        
        // Fallback for other supplier IDs
        reset(defaultValues);
        setDocuments([]);
        setCertifications([]);
        setLoading(false);
      }
    } else {
      reset(defaultValues);
      setDocuments([]);
      setCertifications([]);
    }
  }, [supplierId, reset]);

  // Auto-generate slug when business_name changes
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "business_name" && !supplierId) {
        const businessName = value.business_name as string || "";
        const generated = slugify(businessName);
        const currentSlug = value.slug as string || "";
        if (!currentSlug || currentSlug === slugify("")) {
          setValue("slug", generated);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, supplierId]);

  const onSubmit = async (values: SupplierProfile) => {
    setLoading(true);
    setSlugError(null);

    // Validate slug format
    const slugPattern = /^[a-z0-9-]+$/;
    if (!values.slug || !slugPattern.test(values.slug)) {
      setSlugError("Slug must be lowercase, alphanumeric, and may contain hyphens.");
      setLoading(false);
      return;
    }

    try {
      // Temporarily disabled due to RLS infinite recursion
      console.log("Form submission temporarily disabled due to RLS issues");
      console.log("Form data:", values);
      
      /*
      // Check slug uniqueness
      const { data: existing } = await supabase
        .from("supplier_profiles")
        .select("id")
        .eq("slug", values.slug)
        .neq("id", supplierId || "")
        .maybeSingle();

      if (existing) {
        setSlugError("Slug is already in use. Please choose another.");
        setLoading(false);
        return;
      }

      let result;
      if (supplierId) {
        result = await supabase
          .from("supplier_profiles")
          .update(values)
          .eq("id", supplierId);
      } else {
        result = await supabase
          .from("supplier_profiles")
          .insert([values]);
      }

      if (result.error) throw result.error;
      */
      
      showToast("Supplier profile saved (simulated)", "success");
      if (onChange) onChange();
    } catch (e: unknown) {
      showToast((e as Error).message || "Failed to save.", "error");
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!supplierId) return;
    if (!window.confirm("Are you sure you want to delete this supplier profile? This will also remove all associated documents and certifications.")) return;
    
    setLoading(true);
    const { error } = await supabase.from("supplier_profiles").delete().eq("id", supplierId);
    if (error) showToast(error.message, "error");
    else {
      showToast("Supplier deleted.", "success");
      if (onChange) onChange();
    }
    setLoading(false);
  };

  return (
    <form className="max-w-2xl space-y-6" onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Supplier Selection */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Supplier Selection</h3>
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none">
              Select Supplier
            </label>
            <Controller
              control={control}
              name="supplier_id"
              rules={{ required: "Please select a supplier" }}
              render={({ field }) => (
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  value={field.value}
                  disabled={!!supplierId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name} ({supplier.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.supplier_id && (
              <p className="text-sm text-red-500">{errors.supplier_id.message}</p>
            )}
          </div>
        </div>

        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Basic Information</h3>
          <FormInput 
            label="Business Name" 
            {...register("business_name", { required: true })} 
            error={errors.business_name} 
          />
          <FormInput 
            label="Website" 
            {...register("website")} 
            error={errors.website} 
          />
          <FormTextarea 
            label="Description" 
            {...register("description")} 
            error={errors.description} 
          />
          <FormInput 
            label="Address" 
            {...register("address")} 
            error={errors.address} 
          />
        </div>

        {/* Business Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Business Details</h3>
          <FormInput 
            label="GST Number" 
            {...register("gst_number")} 
            error={errors.gst_number} 
          />
          <FormInput 
            label="PAN Number" 
            {...register("pan_number")} 
            error={errors.pan_number} 
          />
          <FormInput 
            label="Import Export Code" 
            {...register("import_export_code")} 
            error={errors.import_export_code} 
          />
          <Controller
            control={control}
            name="gst_registration_date"
            render={({ field }) => (
              <DatePicker
                label="GST Registration Date"
                value={field.value ? new Date(field.value) : undefined}
                onChange={(date) => field.onChange(date?.toISOString().split('T')[0])}
              />
            )}
          />
        </div>

        {/* Additional Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Additional Information</h3>
          <FormInput 
            label="Establishment Year" 
            type="number"
            {...register("establishment_year")} 
            error={errors.establishment_year} 
          />
          <FormInput 
            label="Legal Status" 
            {...register("legal_status")} 
            error={errors.legal_status} 
          />
          <FormInput 
            label="Nature of Business" 
            {...register("nature_of_business")} 
            error={errors.nature_of_business} 
          />
          <FormInput 
            label="Annual Turnover" 
            {...register("annual_turnover")} 
            error={errors.annual_turnover} 
          />
          <FormInput 
            label="Employee Count" 
            {...register("employee_count")} 
            error={errors.employee_count} 
          />
        </div>

        {/* Company Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Company Information</h3>
          <FormTextarea 
            label="About" 
            {...register("about")} 
            error={errors.about} 
          />
          <FormTextarea 
            label="Mission" 
            {...register("mission")} 
            error={errors.mission} 
          />
          <FormTextarea 
            label="Vision" 
            {...register("vision")} 
            error={errors.vision} 
          />
          <Controller
            control={control}
            name="main_markets"
            render={({ field }) => (
              <MultiSelect
                label="Main Markets"
                value={field.value}
                onChange={field.onChange}
                options={[
                  { label: "Domestic", value: "domestic" },
                  { label: "International", value: "international" },
                  { label: "North America", value: "north_america" },
                  { label: "Europe", value: "europe" },
                  { label: "Asia", value: "asia" },
                  { label: "Africa", value: "africa" },
                  { label: "Australia", value: "australia" }
                ]}
              />
            )}
          />
        </div>

        {/* Verification */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Verification</h3>
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none">
              Verification Status
            </label>
            <Controller
              control={control}
              name="verification_status"
              render={({ field }) => (
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  value={field.value}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select verification status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
          <div className="space-y-2">
            <Controller
              control={control}
              name="trust_seal_verified"
              render={({ field }) => (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <label className="text-sm font-medium leading-none">
                    Trust Seal Verified
                  </label>
                </div>
              )}
            />
          </div>
          <div className="space-y-2">
            <Controller
              control={control}
              name="machinesearch_certification"
              render={({ field }) => (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <label className="text-sm font-medium leading-none">
                    MachineSearch Certified
                  </label>
                </div>
              )}
            />
          </div>
        </div>

        {/* Slug */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium">URL Settings</h3>
          <FormInput
            label="Slug"
            {...register("slug", { required: true, pattern: /^[a-z0-9-]+$/ })}
            error={errors.slug}
            helperText="Unique, lowercase, no spaces (e.g., my-supplier-name)"
          />
          {slugError && <div className="text-sm text-red-500">{slugError}</div>}
          <Button
            type="button"
            variant="outline"
            onClick={() => setValue("slug", slugify(watch("business_name")))}
            disabled={loading || isSubmitting}
          >
            Auto-generate from name
          </Button>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        {supplierId && (
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={loading || isSubmitting}
          >
            Delete Supplier
          </Button>
        )}
        <Button type="submit" disabled={loading || isSubmitting}>
          {loading || isSubmitting ? "Saving..." : "Save Profile"}
        </Button>
      </div>
    </form>
  );
}
